import React from 'react';
import { Container, Box, Typography } from '@mui/material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import { useRegion } from '../../context/RegionContext';

const PrivacyPolicyPage = () => {
  const seo = getPageSEO('privacy');
  const { contact, region } = useRegion();

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection>
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              Privacy Policy
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, color: 'primary.contrastText' }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
      </HeroSection>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ '& h2': { fontSize: 32, fontWeight: 700, mt: 6, mb: 3 }, '& h3': { fontSize: 24, fontWeight: 600, mt: 4, mb: 2 }, '& p': { mb: 2, lineHeight: 1.8, color: 'text.secondary' } }}>

          <Typography variant="body1">
            FluxERP, a product of <strong>Flux IT Studio LTD</strong> ("we," "our," or "us"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our accounting and ERP platform, in accordance with applicable data protection laws{region === 'UK' ? ', including the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018' : ''}.
          </Typography>

          <Typography variant="h2">Information We Collect</Typography>

          <Typography variant="h3">Personal Information</Typography>
          <Typography variant="body1">
            We collect information that you provide directly to us, including:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Name, email address, and contact information</li>
            <li>Company name and business details</li>
            <li>Payment and billing information</li>
            <li>Account credentials and authentication data</li>
          </Box>

          <Typography variant="h3">Business Data</Typography>
          <Typography variant="body1">
            When you use FluxERP, we collect and store:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Financial records and accounting data</li>
            <li>Inventory and stock information</li>
            <li>Customer and vendor details</li>
            <li>Employee and payroll information</li>
            <li>Transaction history and reports</li>
          </Box>

          <Typography variant="h3">Technical Information</Typography>
          <Typography variant="body1">
            We automatically collect certain information about your device and usage:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>IP address and browser type</li>
            <li>Device information and operating system</li>
            <li>Usage patterns and feature interactions</li>
            <li>Log data and error reports</li>
          </Box>

          <Typography variant="h2">How We Use Your Information</Typography>
          <Typography variant="body1">
            We use the collected information for:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Providing and maintaining our services</li>
            <li>Processing transactions and managing accounts</li>
            <li>Sending important updates and notifications</li>
            <li>Improving our platform and developing new features</li>
            <li>Ensuring security and preventing fraud</li>
            <li>Complying with legal obligations</li>
            <li>Providing customer support</li>
          </Box>

          <Typography variant="h2">Data Security</Typography>
          <Typography variant="body1">
            We implement industry-standard security measures to protect your data:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>End-to-end encryption for data transmission</li>
            <li>Secure data storage with encryption at rest</li>
            <li>JWT-based authentication and authorization</li>
            <li>Role-based access control (RBAC)</li>
            <li>Regular security audits and penetration testing</li>
            <li>Multi-factor authentication options</li>
            <li>Complete data isolation between companies</li>
          </Box>

          <Typography variant="h2">Data Sharing and Disclosure</Typography>
          <Typography variant="body1">
            We do not sell your personal information. We may share your data only in the following circumstances:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>With your explicit consent</li>
            <li>With service providers who assist in our operations</li>
            <li>To comply with legal obligations or court orders</li>
            <li>To protect our rights and prevent fraud</li>
            <li>In connection with a business transfer or merger</li>
          </Box>

          <Typography variant="h2">Data Retention</Typography>
          <Typography variant="body1">
            We retain your information for as long as your account is active or as needed to provide services. After account termination, we retain data for 30 days before permanent deletion, unless required by law to retain longer.
          </Typography>

          <Typography variant="h2">Your Rights</Typography>
          <Typography variant="body1">
            You have the right to:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>Access your personal information</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Export your data in a portable format</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent at any time</li>
          </Box>

          <Typography variant="h2">Cookies and Tracking</Typography>
          <Typography variant="body1">
            We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookie preferences through your browser settings.
          </Typography>

          <Typography variant="h2">International Data Transfers</Typography>
          <Typography variant="body1">
            Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
          </Typography>

          <Typography variant="h2">Children's Privacy</Typography>
          <Typography variant="body1">
            FluxERP is not intended for users under 18 years of age. We do not knowingly collect information from children.
          </Typography>

          <Typography variant="h2">Changes to This Policy</Typography>
          <Typography variant="body1">
            We may update this Privacy Policy periodically. We will notify you of significant changes via email or through our platform. Continued use of our services after changes constitutes acceptance of the updated policy.
          </Typography>

          <Typography variant="h2">Contact Us</Typography>
          <Typography variant="body1">
            If you have questions about this Privacy Policy or our data practices, please contact us at:
          </Typography>
          <Box sx={{ mt: 2, p: 3, bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(22, 51, 0, 0.05)', borderRadius: 2, border: '1px solid', borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(22, 51, 0, 0.12)', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>Email: {contact.email}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>Phone: {contact.phone}</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>Address: {contact.addressLine1}, {contact.addressLine2}</Typography>
          </Box>
        </Box>
      </Container>
    </WebsiteLayout>
  );
};

export default PrivacyPolicyPage;
